import React, { useState } from 'react';
import {
  Upload,
  Download,
  FileText,
  CheckCircle,
  AlertTriangle,
  X,
  Loader,
  Info,
  Users,
  Globe
} from 'lucide-react';

const AdminBulkMonitorUpload = ({ onClose, selectedUser = null }) => {
  const [uploadType, setUploadType] = useState(selectedUser ? 'user' : 'global');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const downloadTemplate = (type = 'user') => {
    let content;
    let filename;

    if (type === 'global') {
      content = `user_email,name,url,check_interval,timeout
user@example.com,My Website,https://example.com,300,30
user@example.com,API Server,https://api.example.com,60,15
another@example.com,Blog,https://blog.example.com,300,30
another@example.com,E-commerce,https://shop.example.com,120,45`;
      filename = 'admin_global_monitors_template.csv';
    } else {
      content = `name,url,check_interval,timeout
My Website,https://example.com,300,30
API Server,https://api.example.com,60,15
Blog,https://blog.example.com,300,30
E-commerce Store,https://shop.example.com,120,45`;
      filename = 'admin_user_monitors_template.csv';
    }

    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('adminToken');
      let url;

      if (uploadType === 'global') {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-monitors-global`;
      } else if (selectedUser) {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser.id}/bulk-monitors`;
      } else {
        setError('Please select a user or use global upload');
        setUploading(false);
        return;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Upload failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setError('');
    const fileInput = document.getElementById('admin-csv-upload');
    if (fileInput) fileInput.value = '';
  };

  const renderGlobalResults = () => {
    if (!result || !result.results) return null;

    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="font-medium text-green-800">Global Upload Complete!</h3>
          </div>
          
          <div className="text-sm text-green-700">
            <p>{result.total_users_processed} users processed</p>
            {result.errors && result.errors.length > 0 && (
              <p>{result.errors.length} errors occurred</p>
            )}
          </div>
        </div>

        {/* Per-User Results */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Results by User:</h4>
          {result.results.map((userResult, index) => (
            <div key={index} className="bg-gray-50 border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium text-sm">{userResult.user_name}</div>
                  <div className="text-xs text-gray-500">{userResult.user_email}</div>
                </div>
                <div className="text-xs text-gray-500">
                  {userResult.result.successful} / {userResult.result.total_processed} successful
                </div>
              </div>
              
              {userResult.result.successful > 0 && (
                <div className="text-xs text-green-600 mb-1">
                  ✓ {userResult.result.successful} monitors created
                </div>
              )}
              
              {userResult.result.errors && userResult.result.errors.length > 0 && (
                <div className="text-xs text-red-600">
                  {userResult.result.errors.slice(0, 2).map((error, i) => (
                    <div key={i}>• {error}</div>
                  ))}
                  {userResult.result.errors.length > 2 && (
                    <div>• ... and {userResult.result.errors.length - 2} more errors</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Global Errors */}
        {result.errors && result.errors.length > 0 && (
          <div className="bg-red-50 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-3">Global Errors ({result.errors.length})</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {result.errors.map((error, index) => (
                <div key={index} className="text-sm text-red-700 bg-white p-2 rounded border">
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUserResults = () => {
    if (!result) return null;

    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="font-medium text-green-800">Upload Complete!</h3>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{result.total_processed}</div>
              <div className="text-gray-600">Total Processed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{result.successful}</div>
              <div className="text-gray-600">Successful</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{result.failed}</div>
              <div className="text-gray-600">Failed</div>
            </div>
          </div>
        </div>

        {/* Created Monitors */}
        {result.created_monitors && result.created_monitors.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Created Monitors ({result.created_monitors.length})</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {result.created_monitors.map((monitor, index) => (
                <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                  <div>
                    <div className="font-medium text-sm">{monitor.name}</div>
                    <div className="text-xs text-gray-500">{monitor.url}</div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {monitor.check_interval}s interval
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Errors */}
        {result.errors && result.errors.length > 0 && (
          <div className="bg-red-50 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-3">Errors ({result.errors.length})</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {result.errors.map((error, index) => (
                <div key={index} className="text-sm text-red-700 bg-white p-2 rounded border">
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Upload className="w-6 h-6 text-blue-600 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">Admin Bulk Monitor Upload</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Upload Type Selection */}
          {!selectedUser && !result && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">Upload Type:</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setUploadType('global')}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    uploadType === 'global'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <Globe className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium">Global Upload</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Upload monitors for multiple users using CSV with user_email column
                  </p>
                </button>

                <button
                  onClick={() => setUploadType('user')}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    uploadType === 'user'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <Users className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium">Single User Upload</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Upload monitors for a specific user (select from user management)
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Selected User Info */}
          {selectedUser && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Users className="w-5 h-5 text-blue-600 mr-2" />
                <div>
                  <p className="font-medium text-blue-800">Uploading for: {selectedUser.name}</p>
                  <p className="text-sm text-blue-600">{selectedUser.email} - {selectedUser.subscription_tier} plan</p>
                </div>
              </div>
            </div>
          )}

          {/* Template Download */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium text-blue-800 mb-2">CSV Template</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Download the appropriate CSV template for your upload type.
                </p>
                <div className="flex space-x-2">
                  {(uploadType === 'global' || !selectedUser) && (
                    <button
                      onClick={() => downloadTemplate('global')}
                      className="flex items-center text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Global Template
                    </button>
                  )}
                  {(uploadType === 'user' || selectedUser) && (
                    <button
                      onClick={() => downloadTemplate('user')}
                      className="flex items-center text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      User Template
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CSV Format Instructions */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">CSV Format:</h3>
            <div className="text-sm text-gray-700 space-y-2">
              {uploadType === 'global' && (
                <>
                  <div className="flex">
                    <span className="font-medium text-gray-900 w-32 flex-shrink-0">user_email:</span>
                    <span>Email address of the user to assign monitors to</span>
                  </div>
                </>
              )}
              <div className="flex">
                <span className="font-medium text-gray-900 w-32 flex-shrink-0">name:</span>
                <span>Display name for the monitor</span>
              </div>
              <div className="flex">
                <span className="font-medium text-gray-900 w-32 flex-shrink-0">url:</span>
                <span>Website URL (http/https optional)</span>
              </div>
              <div className="flex">
                <span className="font-medium text-gray-900 w-32 flex-shrink-0">check_interval:</span>
                <span>Check frequency in seconds (default: 300, minimum: 30)</span>
              </div>
              <div className="flex">
                <span className="font-medium text-gray-900 w-32 flex-shrink-0">timeout:</span>
                <span>Request timeout in seconds (default: 30, min: 5, max: 300)</span>
              </div>
            </div>
          </div>

          {/* File Upload */}
          {!result && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select CSV File</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Upload a CSV file with monitor data
                  </p>
                </div>

                <div>
                  <input
                    id="admin-csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label
                    htmlFor="admin-csv-upload"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose CSV File
                  </label>
                </div>

                {file && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-center">
                      <FileText className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm text-green-700">{file.name}</span>
                      <button
                        onClick={resetUpload}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Upload Results */}
          {result && (
            <div className="mb-6">
              {uploadType === 'global' ? renderGlobalResults() : renderUserResults()}
            </div>
          )}

          {/* Admin Privileges Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Admin Privileges:</p>
                <p>
                  As an admin, you can bypass subscription limits when uploading monitors. 
                  Use this power responsibly and consider user subscription tiers when appropriate.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            {result ? (
              <>
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Upload Another File
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading || (uploadType === 'user' && !selectedUser)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <div className="flex items-center">
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Monitors
                    </div>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBulkMonitorUpload;